/**
 * <christmas-builder>: the Christmas design-first apparel builder
 * (sections/christmas-builder.liquid). A plain custom element with no imports, like
 * assets/christmas-shop.js.
 *
 * The designs are the cards the section renders; the garments, with their colors, sizes,
 * variants, prices and pictures, are the JSON the section prints. The script sorts the
 * cards and shows them a page at a time, shows the color and size choices of the chosen
 * garment, draws the preview, keeps the choice in the address, and fills the form's variant
 * id and "Design" property (the design's name) only when everything is chosen. The form then
 * posts to the cart the normal way.
 *
 * Design names are not shown on the page; the chosen one is put in visually hidden text
 * (the preview caption and the start of the summary) so screen readers still say it.
 */

/**
 * @typedef {object} BuilderVariant
 * @property {number} id
 * @property {string} color - '' when the garment has no color option
 * @property {string} size - '' when the garment has no size option
 * @property {number} price - in cents
 * @property {boolean} available
 *
 * @typedef {object} BuilderColor
 * @property {string} name
 * @property {string} swatch - a CSS color, or '' to use the name
 * @property {string} image - the garment's picture in this color, or ''
 *
 * @typedef {object} BuilderGarment
 * @property {string} handle
 * @property {string} title
 * @property {string} image
 * @property {number} top - percent
 * @property {number} left - percent, the center of the design
 * @property {number} width - percent
 * @property {BuilderColor[]} colors
 * @property {string[]} sizes
 * @property {BuilderVariant[]} variants
 */

const same = (/** @type {string} */ a, /** @type {string} */ b) => a.trim().toLowerCase() === b.trim().toLowerCase();

class ChristmasBuilder extends HTMLElement {
  #abort = new AbortController();
  /** @type {BuilderGarment[]} */
  #garments = [];
  /** @type {HTMLElement[]} the design cards, in design order */
  #cards = [];
  #shown = 0;
  /** @type {HTMLElement | null} */
  #design = null;
  /** @type {BuilderGarment | null} */
  #garment = null;
  #color = '';
  #size = '';

  connectedCallback() {
    const source = this.querySelector('script[data-garment-data]');
    try {
      this.#garments = JSON.parse(source?.textContent || '[]');
    } catch (error) {
      return;
    }
    const form = this.querySelector('[data-form]');
    if (!(form instanceof HTMLFormElement)) return;
    const { signal } = this.#abort;

    const list = this.querySelector('[data-designs]');
    this.#cards = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-card]')]).sort(
      (a, b) => Number(a.dataset.order) - Number(b.dataset.order)
    );
    if (list) list.append(...this.#cards);

    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.querySelector('[data-quantity]')?.addEventListener('input', () => this.#showPrice(), { signal });
    form.addEventListener('submit', (event) => this.#onSubmit(event), { signal });

    this.#readAddress();
    this.#page(true);
    this.#showGarment();
    this.querySelector('[data-fallback]')?.remove();
    form.hidden = false;
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
  }

  // --- The address ------------------------------------------------------------------------

  #readAddress() {
    const params = new URL(window.location.href).searchParams;
    const design = params.get('design');
    if (design) this.#design = this.#cards.find((card) => card.dataset.handle === design) || null;
    const garment = params.get('garment');
    if (garment) this.#garment = this.#garments.find((item) => item.handle === garment) || null;
    if (this.#garment) {
      const color = params.get('color') || '';
      const found = this.#garment.colors.find((item) => same(item.name, color));
      if (found) this.#color = found.name;
      const size = params.get('size') || '';
      const foundSize = this.#garment.sizes.find((item) => same(item, size));
      if (foundSize && this.#variant(this.#color, foundSize)?.available) this.#size = foundSize;
    }
  }

  #writeAddress() {
    const url = new URL(window.location.href);
    const set = (/** @type {string} */ key, /** @type {string} */ value) => {
      if (value) url.searchParams.set(key, value);
      else url.searchParams.delete(key);
    };
    set('design', this.#design?.dataset.handle || '');
    set('garment', this.#garment?.handle || '');
    set('color', this.#color.toLowerCase());
    set('size', this.#size);
    window.history.replaceState(window.history.state, '', url);
  }

  // --- Designs ---------------------------------------------------------------------------

  /** @param {boolean} reset - start again from the first page */
  #page(reset) {
    const perPage = Number(this.dataset.perPage) || 24;
    const cards = this.#cards;
    if (reset) this.#shown = perPage;
    // A chosen design is always shown, so it can be seen as chosen.
    const chosenAt = this.#design ? cards.indexOf(this.#design) : -1;
    if (reset && chosenAt >= this.#shown) this.#shown = Math.ceil((chosenAt + 1) / perPage) * perPage;
    const visible = new Set(cards.slice(0, this.#shown));
    for (const card of cards) card.hidden = !visible.has(card);

    const count = this.querySelector('[data-count-text]');
    if (count) {
      count.textContent = (this.dataset.count || '')
        .replace('[shown]', String(visible.size))
        .replace('[total]', String(cards.length));
    }
    const more = this.querySelector('[data-more]');
    if (more instanceof HTMLElement) more.hidden = cards.length <= this.#shown;
    this.#showDesign();
  }

  #showDesign() {
    for (const card of this.#cards) {
      card.querySelector('[data-design]')?.setAttribute('aria-pressed', String(card === this.#design));
    }
    this.#showPreview();
  }

  // --- Garment, color, size ----------------------------------------------------------------

  /** @param {string} color @param {string} size */
  #variant(color, size) {
    const garment = this.#garment;
    if (!garment) return undefined;
    return garment.variants.find(
      (variant) =>
        (!garment.colors.length || same(variant.color, color)) && (!garment.sizes.length || same(variant.size, size))
    );
  }

  /** @param {string} color */
  #colorHasStock(color) {
    return !!this.#garment?.variants.some((variant) => same(variant.color, color) && variant.available);
  }

  #showGarment() {
    for (const button of this.querySelectorAll('[data-garment]')) {
      button.setAttribute('aria-pressed', String(/** @type {HTMLElement} */ (button).dataset.garment === this.#garment?.handle));
    }
    const garment = this.#garment;
    const empty = this.querySelector('[data-options-empty]');
    if (empty instanceof HTMLElement) empty.hidden = !!garment;

    // Colors: only the ones this garment has.
    const colorGroup = this.querySelector('[data-color-group]');
    const colors = this.querySelector('[data-colors]');
    if (colorGroup instanceof HTMLElement && colors) {
      colorGroup.hidden = !garment || garment.colors.length === 0;
      colors.replaceChildren(
        ...(garment?.colors || []).map((color) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'xmas-builder__color';
          button.dataset.color = color.name;
          button.setAttribute('aria-pressed', String(same(color.name, this.#color)));
          const swatch = document.createElement('span');
          swatch.className = 'xmas-builder__swatch';
          swatch.setAttribute('aria-hidden', 'true');
          swatch.style.setProperty('--swatch', color.swatch || color.name.toLowerCase());
          const name = document.createElement('span');
          name.textContent = color.name;
          button.append(swatch, name);
          if (!this.#colorHasStock(color.name)) {
            button.disabled = true;
            name.textContent = color.name + ' (' + (this.dataset.soldOut || '') + ')';
          }
          return button;
        })
      );
    }
    const colorName = this.querySelector('[data-color-name]');
    if (colorName) colorName.textContent = this.#color;

    // Sizes: the ones that exist in this color; sold-out ones are switched off.
    const sizeGroup = this.querySelector('[data-size-group]');
    const sizes = this.querySelector('[data-sizes]');
    const needsColor = !!garment && garment.colors.length > 0 && !this.#color;
    if (sizeGroup instanceof HTMLElement && sizes) {
      sizeGroup.hidden = !garment || garment.sizes.length === 0 || needsColor;
      sizes.replaceChildren(
        ...(garment && !needsColor ? garment.sizes : [])
          .map((size) => ({ size, variant: this.#variant(this.#color, size) }))
          .filter((item) => item.variant)
          .map(({ size, variant }) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'xmas-builder__size';
            button.dataset.size = size;
            button.textContent = size;
            button.setAttribute('aria-pressed', String(same(size, this.#size)));
            if (!variant?.available) {
              button.disabled = true;
              button.setAttribute('aria-label', size + ', ' + (this.dataset.soldOut || ''));
            }
            return button;
          })
      );
    }
    const sizeName = this.querySelector('[data-size-name]');
    if (sizeName) sizeName.textContent = this.#size;
    this.#showPreview();
    this.#showPrice();
  }

  // --- Preview, summary and price -------------------------------------------------------------

  #showPreview() {
    const garment = this.#garment;
    const stage = this.querySelector('[data-stage]');
    const garmentImage = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-stage-garment]'));
    const designImage = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-stage-design]'));
    const empty = this.querySelector('[data-stage-empty]');
    if (!(stage instanceof HTMLElement) || !garmentImage || !designImage) return;

    const color = garment?.colors.find((item) => same(item.name, this.#color));
    const garmentSource = color?.image || garment?.image || '';
    garmentImage.hidden = !garmentSource;
    if (garmentSource && garmentImage.getAttribute('src') !== garmentSource) garmentImage.src = garmentSource;
    garmentImage.alt = garment ? [garment.title, this.#color].filter(Boolean).join(', ') : '';

    const card = this.#design;
    const dark = (this.dataset.darkColors || '').split(',').some((name) => name && same(name, this.#color));
    const designSource = card ? (dark && card.dataset.dark) || card.dataset.image || '' : '';
    designImage.hidden = !designSource;
    if (designSource && designImage.getAttribute('src') !== designSource) designImage.src = designSource;
    designImage.alt = card?.dataset.name || '';
    if (garment) {
      stage.style.setProperty('--design-top', garment.top + '%');
      stage.style.setProperty('--design-left', garment.left + '%');
      stage.style.setProperty('--design-width', garment.width + '%');
    }
    if (empty instanceof HTMLElement) empty.hidden = !!garmentSource || !!designSource;

    const caption = this.querySelector('[data-stage-caption]');
    const picked = card
      ? (this.dataset.pickedDesign || '[name]').replace('[name]', card.dataset.name || '')
      : this.dataset.pickedNone || '';
    if (caption) caption.textContent = picked;
    // The summary: "Design: <name>" for screen readers only, then the garment, color and size
    // for everyone. Before a design is chosen, "No design picked yet" shows as a reminder.
    const summary = this.querySelector('[data-summary]');
    if (summary) {
      const rest = [garment?.title, this.#color, this.#size].filter(Boolean);
      if (card) {
        const hidden = document.createElement('span');
        hidden.className = 'visually-hidden';
        hidden.textContent = rest.length ? picked + ' · ' : picked;
        summary.replaceChildren(hidden, rest.join(' · '));
      } else {
        summary.textContent = [picked, ...rest].join(' · ');
      }
    }
  }

  /** @param {number} cents */
  #money(cents) {
    try {
      return new Intl.NumberFormat(this.dataset.locale || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      }).format(cents / 100);
    } catch (error) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  #quantity() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-quantity]'));
    const n = Math.floor(Number(input?.value));
    return Number.isFinite(n) && n > 0 ? Math.min(n, 99) : 1;
  }

  // The chosen variant's price times the quantity; before that, the garment's lowest price.
  #showPrice() {
    const price = this.querySelector('[data-price]');
    if (!price) return;
    const garment = this.#garment;
    if (!garment) {
      price.textContent = '';
      return;
    }
    const variant = this.#variant(this.#color, this.#size);
    if (variant && (this.#size || !garment.sizes.length) && (this.#color || !garment.colors.length)) {
      price.textContent = this.#money(variant.price * this.#quantity());
      return;
    }
    const prices = garment.variants.filter((item) => item.available).map((item) => item.price);
    const lowest = prices.length ? Math.min(...prices) : 0;
    const varies = prices.some((item) => item !== lowest);
    price.textContent = varies ? (this.dataset.from || '[price]').replace('[price]', this.#money(lowest)) : this.#money(lowest);
  }

  // --- Events ------------------------------------------------------------------------------

  /** @param {Event} event */
  #onClick(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const design = target.closest('[data-design]');
    if (design instanceof HTMLElement) {
      this.#design = /** @type {HTMLElement | null} */ (design.closest('[data-card]'));
      this.#showDesign();
      this.#done();
      return;
    }
    const garment = target.closest('[data-garment]');
    if (garment instanceof HTMLElement) {
      const chosen = this.#garments.find((item) => item.handle === garment.dataset.garment) || null;
      if (chosen !== this.#garment) {
        this.#garment = chosen;
        // Keep the color when the new garment has it in stock; the size is chosen again.
        const keep = chosen?.colors.find((item) => same(item.name, this.#color));
        this.#color = keep && this.#colorHasStock(keep.name) ? keep.name : '';
        if (chosen && chosen.colors.length === 1 && this.#colorHasStock(chosen.colors[0].name)) this.#color = chosen.colors[0].name;
        this.#size = '';
      }
      this.#showGarment();
      this.#done();
      return;
    }
    const color = target.closest('[data-color]');
    if (color instanceof HTMLButtonElement && !color.disabled) {
      this.#color = color.dataset.color || '';
      if (this.#size && !this.#variant(this.#color, this.#size)?.available) this.#size = '';
      this.#showGarment();
      this.#done();
      this.querySelector(`[data-color="${CSS.escape(this.#color)}"]`)?.focus();
      return;
    }
    const size = target.closest('[data-size]');
    if (size instanceof HTMLButtonElement && !size.disabled) {
      this.#size = size.dataset.size || '';
      this.#showGarment();
      this.#done();
      this.querySelector(`[data-size="${CSS.escape(this.#size)}"]`)?.focus();
      return;
    }
    if (target.closest('[data-more]')) {
      const first = this.#cards.filter((card) => !card.hidden).length;
      this.#shown += Number(this.dataset.perPage) || 24;
      this.#page(false);
      // Move focus to the first new card, so keyboard users carry on from there.
      this.#cards.filter((card) => !card.hidden)[first]?.querySelector('button')?.focus();
    }
  }

  // After every choice: save it in the address and clear an old message.
  #done() {
    this.#writeAddress();
    this.#say('');
  }

  /** @param {string} text */
  #say(text) {
    const message = this.querySelector('[data-message]');
    if (!(message instanceof HTMLElement)) return;
    message.textContent = text;
    message.hidden = !text;
  }

  /** @param {SubmitEvent} event */
  #onSubmit(event) {
    const garment = this.#garment;
    /** @type {[string, Element | null] | null} */
    let problem = null;
    if (!this.#design) problem = [this.dataset.needDesign || '', this.querySelector('[data-card]:not([hidden]) [data-design]')];
    else if (!garment) problem = [this.dataset.needGarment || '', this.querySelector('[data-garment]')];
    else if (garment.colors.length && !this.#color) problem = [this.dataset.needColor || '', this.querySelector('[data-color]:not(:disabled)')];
    else if (garment.sizes.length && !this.#size) problem = [this.dataset.needSize || '', this.querySelector('[data-size]:not(:disabled)')];
    const variant = problem ? undefined : this.#variant(this.#color, this.#size);
    if (!problem && (!variant || !variant.available)) problem = [this.dataset.unavailable || '', this.querySelector('[data-size]:not(:disabled)')];
    if (problem || !variant) {
      event.preventDefault();
      this.#say(problem ? problem[0] : this.dataset.unavailable || '');
      if (problem?.[1] instanceof HTMLElement) problem[1].focus();
      return;
    }
    // Only now, with everything chosen, does the form get the variant and the design.
    const id = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-variant-input]'));
    const design = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-design-input]'));
    if (!id || !design) {
      event.preventDefault();
      return;
    }
    id.value = String(variant.id);
    design.value = this.#design?.dataset.name || '';
    const quantity = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-quantity]'));
    if (quantity) quantity.value = String(this.#quantity());
  }
}

if (!customElements.get('christmas-builder')) customElements.define('christmas-builder', ChristmasBuilder);
